/**
 * 标准重合配合中的解析曲面语义。
 *
 * 文件职责：
 * 1. 独立处理 SOLIDWORKS Coincident Mate 中点/线/圆边与圆柱、圆锥、球面的真实几何关系；
 * 2. 避免把 Coincident 错误退化为 Concentric 的中心线/中心点重合；
 * 3. 为求解器与最终残差校验器提供同一套纯几何函数，保持业务层与 UI 零耦合。
 */

import * as THREE from 'three';
import {
  AssemblyMateType,
  MateAlignment,
  MateSolveStatus,
} from '../definition';
import type {
  StandardMateApplyResult,
  StandardMateMovingSideResolution,
} from './StandardMateSolverTypes';
import {
  applyWorldDeltaToTransform,
  createRotationBetweenDirections,
  createRotationDeltaAroundPoint,
  createTranslationDelta,
  normalizeVector,
  resolveAxisGeometry,
  resolveConeGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
  type ConeGeometry,
  type PointGeometry,
  type SphereGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  isCircularEdgeEntity,
  isConeEntity,
  isCylinderEntity,
  isLinearAxisEntity,
  isSphereEntity,
  projectPointToAxis,
  resolveAxisRadius,
  resolveConeAxialDistanceForEdgeRadius,
} from './geometry/StandardMateSurfaceCoincidentGeometry';

/**
 * 构造与指定方向稳定垂直的单位向量。
 *
 * @param direction 源方向。
 * @param epsilon 容差。
 * @returns 垂直方向。
 */
function resolvePerpendicularDirection(direction: THREE.Vector3, epsilon: number): THREE.Vector3 {
  const seed = Math.abs(direction.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const perpendicular = direction.clone().cross(seed);
  return normalizeVector(perpendicular, epsilon) ?? new THREE.Vector3(0, 0, 1);
}

/**
 * 将轴线按世界矩阵变换。
 *
 * @param axis 原轴线。
 * @param matrix 世界变换矩阵。
 * @returns 变换后的轴线。
 */
function transformAxis(axis: AxisGeometry, matrix: THREE.Matrix4): AxisGeometry {
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
  return {
    ...axis,
    origin: axis.origin.clone().applyMatrix4(matrix),
    direction: axis.direction.clone().applyMatrix3(normalMatrix).normalize(),
  };
}

/**
 * 创建平移求解结果。
 *
 * @param side 当前移动侧。
 * @param delta 世界平移量。
 * @returns 单条配合求解结果。
 */
function createTranslationResult(
  side: StandardMateMovingSideResolution,
  delta: THREE.Vector3,
): StandardMateApplyResult {
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, createTranslationDelta(delta)),
  };
}

/**
 * 创建不支持几何的结果。
 *
 * @param message 诊断信息。
 * @returns 单条配合求解结果。
 */
function createUnsupportedResult(message: string): StandardMateApplyResult {
  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message,
  };
}

/**
 * 创建无法求解的结果。
 *
 * @param message 诊断信息。
 * @returns 单条配合求解结果。
 */
function createUnsolvedResult(message: string): StandardMateApplyResult {
  return {
    status: MateSolveStatus.Unsolved,
    message,
  };
}

/**
 * 求解点落在固定圆柱面上。
 *
 * @param side 当前移动侧。
 * @param point 移动点。
 * @param cylinder 固定圆柱轴线。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingPointOnFixedCylinder(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  cylinder: AxisGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const radius = resolveAxisRadius(cylinder);
  if (radius === null) {
    return createUnsupportedResult('圆柱重合配合缺少可求解半径。');
  }

  const projection = projectPointToAxis(point.point, cylinder);
  const radial = point.point.clone().sub(projection);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(cylinder.direction, epsilon);
  const targetPoint = projection.addScaledVector(radialDirection, radius);
  return createTranslationResult(side, targetPoint.sub(point.point));
}

/**
 * 求解移动圆柱面包含固定点。
 *
 * @param side 当前移动侧。
 * @param point 固定点。
 * @param cylinder 移动圆柱轴线。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingCylinderOnFixedPoint(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  cylinder: AxisGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const radius = resolveAxisRadius(cylinder);
  if (radius === null) {
    return createUnsupportedResult('圆柱重合配合缺少可求解半径。');
  }

  const projection = projectPointToAxis(point.point, cylinder);
  const radial = projection.clone().sub(point.point);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(cylinder.direction, epsilon);
  const targetProjection = point.point.clone().addScaledVector(radialDirection, radius);
  return createTranslationResult(side, targetProjection.sub(projection));
}

/**
 * 求解点落在固定球面上。
 *
 * @param side 当前移动侧。
 * @param point 移动点。
 * @param sphere 固定球面。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingPointOnFixedSphere(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  sphere: SphereGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const direction = normalizeVector(point.point.clone().sub(sphere.center), epsilon)
    ?? new THREE.Vector3(1, 0, 0);
  const targetPoint = sphere.center.clone().addScaledVector(direction, sphere.radius);
  return createTranslationResult(side, targetPoint.sub(point.point));
}

/**
 * 求解移动球面包含固定点。
 *
 * @param side 当前移动侧。
 * @param point 固定点。
 * @param sphere 移动球面。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingSphereOnFixedPoint(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  sphere: SphereGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const direction = normalizeVector(point.point.clone().sub(sphere.center), epsilon)
    ?? new THREE.Vector3(1, 0, 0);
  const targetCenter = point.point.clone().addScaledVector(direction, -sphere.radius);
  return createTranslationResult(side, targetCenter.sub(sphere.center));
}

/**
 * 计算点在圆锥侧面上的最近目标点。
 *
 * @param point 当前点。
 * @param cone 圆锥几何。
 * @param epsilon 容差。
 * @returns 圆锥侧面目标点；圆锥参数退化时返回 null。
 */
function resolvePointOnConeSurfaceTarget(
  point: THREE.Vector3,
  cone: ConeGeometry,
  epsilon: number,
): THREE.Vector3 | null {
  const apexToPoint = point.clone().sub(cone.apex);
  const axial = apexToPoint.dot(cone.direction);
  const radialVector = apexToPoint.sub(cone.direction.clone().multiplyScalar(axial));
  const radialLength = radialVector.length();
  const radialDirection = normalizeVector(radialVector, epsilon)
    ?? resolvePerpendicularDirection(cone.direction, epsilon);
  const sideAxial = Math.cos(cone.halfAngle);
  const sideRadial = Math.sin(cone.halfAngle);
  const sideParameter = Math.max(0, axial * sideAxial + radialLength * sideRadial);
  if (!Number.isFinite(sideParameter)) {
    return null;
  }

  return cone.apex
    .clone()
    .addScaledVector(cone.direction, sideParameter * sideAxial)
    .addScaledVector(radialDirection, sideParameter * sideRadial);
}

/**
 * 求解点落在固定圆锥面上。
 *
 * @param side 当前移动侧。
 * @param point 移动点。
 * @param cone 固定圆锥。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingPointOnFixedCone(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  cone: ConeGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const targetPoint = resolvePointOnConeSurfaceTarget(point.point, cone, epsilon);
  if (!targetPoint) {
    return createUnsupportedResult('圆锥重合配合缺少稳定侧面目标点。');
  }
  return createTranslationResult(side, targetPoint.sub(point.point));
}

/**
 * 求解移动圆锥面包含固定点。
 *
 * @param side 当前移动侧。
 * @param point 固定点。
 * @param cone 移动圆锥。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingConeOnFixedPoint(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  cone: ConeGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const targetPoint = resolvePointOnConeSurfaceTarget(point.point, cone, epsilon);
  if (!targetPoint) {
    return createUnsupportedResult('圆锥重合配合缺少稳定侧面目标点。');
  }
  return createTranslationResult(side, point.point.clone().sub(targetPoint));
}

/**
 * 计算点在圆边上的最近目标点。
 *
 * @param point 当前点。
 * @param circle 圆边轴线几何。
 * @param epsilon 容差。
 * @returns 圆边上的目标点；缺少半径时返回 null。
 */
function resolvePointOnCircleTarget(
  point: THREE.Vector3,
  circle: AxisGeometry,
  epsilon: number,
): THREE.Vector3 | null {
  const radius = resolveAxisRadius(circle);
  if (radius === null) {
    return null;
  }

  const centerToPoint = point.clone().sub(circle.origin);
  const inPlaneVector = centerToPoint.sub(circle.direction.clone().multiplyScalar(centerToPoint.dot(circle.direction)));
  const radialDirection = normalizeVector(inPlaneVector, epsilon)
    ?? resolvePerpendicularDirection(circle.direction, epsilon);
  return circle.origin.clone().addScaledVector(radialDirection, radius);
}

/**
 * 求解点落在固定圆边上。
 *
 * @param side 当前移动侧。
 * @param point 移动点。
 * @param circle 固定圆边。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingPointOnFixedCircularEdge(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  circle: AxisGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const targetPoint = resolvePointOnCircleTarget(point.point, circle, epsilon);
  if (!targetPoint) {
    return createUnsupportedResult('点-圆边重合配合缺少可求解圆边半径。');
  }
  return createTranslationResult(side, targetPoint.sub(point.point));
}

/**
 * 求解移动圆边包含固定点。
 *
 * @param side 当前移动侧。
 * @param point 固定点。
 * @param circle 移动圆边。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingCircularEdgeOnFixedPoint(
  side: StandardMateMovingSideResolution,
  point: PointGeometry,
  circle: AxisGeometry,
  epsilon: number,
): StandardMateApplyResult {
  const targetPoint = resolvePointOnCircleTarget(point.point, circle, epsilon);
  if (!targetPoint) {
    return createUnsupportedResult('圆边-点重合配合缺少可求解圆边半径。');
  }
  return createTranslationResult(side, point.point.clone().sub(targetPoint));
}

/**
 * 求解点与解析曲面的重合。
 *
 * @param side 当前移动侧。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
function solvePointSurfaceCoincident(
  side: StandardMateMovingSideResolution,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedPoint = resolvePointGeometry(side.fixedEntity);
  const movingPoint = resolvePointGeometry(side.movingEntity);
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  const fixedSphere = resolveSphereGeometry(side.fixedEntity);
  const movingSphere = resolveSphereGeometry(side.movingEntity);
  const fixedCone = resolveConeGeometry(side.fixedEntity);
  const movingCone = resolveConeGeometry(side.movingEntity);

  if (movingPoint && fixedAxis && isCircularEdgeEntity(side.fixedEntity)) {
    return solveMovingPointOnFixedCircularEdge(side, movingPoint, fixedAxis, epsilon);
  }
  if (fixedPoint && movingAxis && isCircularEdgeEntity(side.movingEntity)) {
    return solveMovingCircularEdgeOnFixedPoint(side, fixedPoint, movingAxis, epsilon);
  }
  if (movingPoint && fixedAxis && isCylinderEntity(side.fixedEntity)) {
    return solveMovingPointOnFixedCylinder(side, movingPoint, fixedAxis, epsilon);
  }
  if (fixedPoint && movingAxis && isCylinderEntity(side.movingEntity)) {
    return solveMovingCylinderOnFixedPoint(side, fixedPoint, movingAxis, epsilon);
  }
  if (movingPoint && fixedSphere && isSphereEntity(side.fixedEntity)) {
    return solveMovingPointOnFixedSphere(side, movingPoint, fixedSphere, epsilon);
  }
  if (fixedPoint && movingSphere && isSphereEntity(side.movingEntity)) {
    return solveMovingSphereOnFixedPoint(side, fixedPoint, movingSphere, epsilon);
  }
  if (movingPoint && fixedCone && isConeEntity(side.fixedEntity)) {
    return solveMovingPointOnFixedCone(side, movingPoint, fixedCone, epsilon);
  }
  if (fixedPoint && movingCone && isConeEntity(side.movingEntity)) {
    return solveMovingConeOnFixedPoint(side, fixedPoint, movingCone, epsilon);
  }

  return null;
}

/**
 * 求解两条轴线按指定距离与方向重合。
 *
 * @param side 当前移动侧。
 * @param targetAxis 固定轴线。
 * @param movingAxis 移动轴线。
 * @param targetDistance 目标轴线距离。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveAxisDistanceWithDirection(
  side: StandardMateMovingSideResolution,
  targetAxis: AxisGeometry,
  movingAxis: AxisGeometry,
  targetDistance: number,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult {
  const targetDirection = mateAlignment === MateAlignment.AntiAligned
    ? targetAxis.direction.clone().multiplyScalar(-1)
    : targetAxis.direction;
  const rotation = createRotationBetweenDirections(movingAxis.direction, targetDirection, epsilon);
  if (!rotation) {
    return createUnsolvedResult('轴向无法对齐到解析曲面重合目标。');
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingAxis.origin, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedAxis = transformAxis(movingAxis, rotationDelta);
  const projection = projectPointToAxis(rotatedAxis.origin, targetAxis);
  const radial = rotatedAxis.origin.clone().sub(projection);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(targetAxis.direction, epsilon);
  const targetOrigin = projection.addScaledVector(radialDirection, targetDistance);

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      rotatedTransform,
      createTranslationDelta(targetOrigin.sub(rotatedAxis.origin)),
    ),
  };
}

/**
 * 求解直线与圆柱面的重合。
 *
 * @param side 当前移动侧。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
function solveLineCylinderCoincident(
  side: StandardMateMovingSideResolution,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  if (!fixedAxis || !movingAxis) {
    return null;
  }

  if (isCylinderEntity(side.fixedEntity) && isLinearAxisEntity(side.movingEntity)) {
    const radius = resolveAxisRadius(fixedAxis);
    return radius === null
      ? createUnsupportedResult('圆柱-线重合配合缺少圆柱半径。')
      : solveAxisDistanceWithDirection(side, fixedAxis, movingAxis, radius, mateAlignment, epsilon);
  }
  if (isLinearAxisEntity(side.fixedEntity) && isCylinderEntity(side.movingEntity)) {
    const radius = resolveAxisRadius(movingAxis);
    return radius === null
      ? createUnsupportedResult('线-圆柱重合配合缺少圆柱半径。')
      : solveAxisDistanceWithDirection(side, fixedAxis, movingAxis, radius, mateAlignment, epsilon);
  }

  return null;
}

/**
 * 求解圆边与圆柱面的重合。
 *
 * @param side 当前移动侧。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
function solveCircularEdgeCylinderCoincident(
  side: StandardMateMovingSideResolution,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  if (!fixedAxis || !movingAxis) {
    return null;
  }

  if (isCylinderEntity(side.fixedEntity) && isCircularEdgeEntity(side.movingEntity)) {
    const fixedRadius = resolveAxisRadius(fixedAxis);
    const movingRadius = resolveAxisRadius(movingAxis);
    if (fixedRadius === null || movingRadius === null) {
      return createUnsupportedResult('圆边-圆柱重合配合缺少半径。');
    }
    if (Math.abs(fixedRadius - movingRadius) > epsilon) {
      return createUnsolvedResult('圆边与圆柱半径不同，无法建立真实重合。');
    }
    return solveAxisDistanceWithDirection(side, fixedAxis, movingAxis, 0, mateAlignment, epsilon);
  }

  if (isCircularEdgeEntity(side.fixedEntity) && isCylinderEntity(side.movingEntity)) {
    const fixedRadius = resolveAxisRadius(fixedAxis);
    const movingRadius = resolveAxisRadius(movingAxis);
    if (fixedRadius === null || movingRadius === null) {
      return createUnsupportedResult('圆柱-圆边重合配合缺少半径。');
    }
    if (Math.abs(fixedRadius - movingRadius) > epsilon) {
      return createUnsolvedResult('圆柱与圆边半径不同，无法建立真实重合。');
    }
    return solveAxisDistanceWithDirection(side, fixedAxis, movingAxis, 0, mateAlignment, epsilon);
  }

  return null;
}

/**
 * 求解圆边落在固定圆锥面上。
 *
 * @param side 当前移动侧。
 * @param cone 固定圆锥。
 * @param edge 移动圆边。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingEdgeOnFixedCone(
  side: StandardMateMovingSideResolution,
  cone: ConeGeometry,
  edge: AxisGeometry,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult {
  const edgeRadius = resolveAxisRadius(edge);
  const axialDistance = edgeRadius === null
    ? null
    : resolveConeAxialDistanceForEdgeRadius(cone, edgeRadius);
  if (axialDistance === null) {
    return createUnsupportedResult('圆边-圆锥重合配合缺少可求解半径或圆锥半角。');
  }

  const targetDirection = mateAlignment === MateAlignment.AntiAligned
    ? cone.direction.clone().multiplyScalar(-1)
    : cone.direction;
  const rotation = createRotationBetweenDirections(edge.direction, targetDirection, epsilon);
  if (!rotation) {
    return createUnsolvedResult('圆边法向无法对齐圆锥轴线。');
  }
  const rotationDelta = createRotationDeltaAroundPoint(edge.origin, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedEdge = transformAxis(edge, rotationDelta);
  const targetCenter = cone.apex.clone().addScaledVector(cone.direction, axialDistance);

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      rotatedTransform,
      createTranslationDelta(targetCenter.sub(rotatedEdge.origin)),
    ),
  };
}

/**
 * 求解移动圆锥面包含固定圆边。
 *
 * @param side 当前移动侧。
 * @param edge 固定圆边。
 * @param cone 移动圆锥。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 单条配合求解结果。
 */
function solveMovingConeOnFixedEdge(
  side: StandardMateMovingSideResolution,
  edge: AxisGeometry,
  cone: ConeGeometry,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult {
  const edgeRadius = resolveAxisRadius(edge);
  const axialDistance = edgeRadius === null
    ? null
    : resolveConeAxialDistanceForEdgeRadius(cone, edgeRadius);
  if (axialDistance === null) {
    return createUnsupportedResult('圆锥-圆边重合配合缺少可求解半径或圆锥半角。');
  }

  const targetDirection = mateAlignment === MateAlignment.AntiAligned
    ? edge.direction.clone().multiplyScalar(-1)
    : edge.direction;
  const rotation = createRotationBetweenDirections(cone.direction, targetDirection, epsilon);
  if (!rotation) {
    return createUnsolvedResult('圆锥轴线无法对齐圆边法向。');
  }
  const rotationDelta = createRotationDeltaAroundPoint(cone.apex, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedCone = {
    ...cone,
    apex: cone.apex.clone().applyMatrix4(rotationDelta),
    direction: cone.direction
      .clone()
      .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
      .normalize(),
  };
  const targetApex = edge.origin.clone().addScaledVector(rotatedCone.direction, -axialDistance);

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      rotatedTransform,
      createTranslationDelta(targetApex.sub(rotatedCone.apex)),
    ),
  };
}

/**
 * 求解圆边与圆锥面的重合。
 *
 * @param side 当前移动侧。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
function solveCircularEdgeConeCoincident(
  side: StandardMateMovingSideResolution,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedCone = resolveConeGeometry(side.fixedEntity);
  const movingCone = resolveConeGeometry(side.movingEntity);
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);

  if (fixedCone && movingAxis && isCircularEdgeEntity(side.movingEntity)) {
    return solveMovingEdgeOnFixedCone(side, fixedCone, movingAxis, mateAlignment, epsilon);
  }
  if (fixedAxis && isCircularEdgeEntity(side.fixedEntity) && movingCone) {
    return solveMovingConeOnFixedEdge(side, fixedAxis, movingCone, mateAlignment, epsilon);
  }

  return null;
}

/**
 * 求解两个圆锥面的重合。
 *
 * @param side 当前移动侧。
 * @param mateAlignment 配合对齐方式。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
function solveConeConeCoincident(
  side: StandardMateMovingSideResolution,
  mateAlignment: MateAlignment | undefined,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedCone = resolveConeGeometry(side.fixedEntity);
  const movingCone = resolveConeGeometry(side.movingEntity);
  if (!fixedCone || !movingCone) {
    return null;
  }
  if (Math.abs(fixedCone.halfAngle - movingCone.halfAngle) > epsilon) {
    return createUnsolvedResult('两个圆锥半角不同，无法建立真实重合。');
  }

  const targetDirection = mateAlignment === MateAlignment.AntiAligned
    ? fixedCone.direction.clone().multiplyScalar(-1)
    : fixedCone.direction;
  const rotation = createRotationBetweenDirections(movingCone.direction, targetDirection, epsilon);
  if (!rotation) {
    return createUnsolvedResult('圆锥轴线无法对齐。');
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingCone.apex, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedApex = movingCone.apex.clone().applyMatrix4(rotationDelta);
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      rotatedTransform,
      createTranslationDelta(fixedCone.apex.clone().sub(rotatedApex)),
    ),
  };
}

/**
 * 求解解析曲面 Coincident Mate。
 *
 * @param mate 当前配合定义。
 * @param side 当前移动侧。
 * @param epsilon 容差。
 * @returns 可求解时返回结果；不属于该语义时返回 null。
 */
export function solveSurfaceCoincidentMate(
  mate: {
    readonly mateType: AssemblyMateType;
    readonly alignment?: MateAlignment;
  },
  side: StandardMateMovingSideResolution,
  epsilon: number,
): StandardMateApplyResult | null {
  if (mate.mateType !== AssemblyMateType.Coincident) {
    return null;
  }

  return solvePointSurfaceCoincident(side, epsilon)
    ?? solveLineCylinderCoincident(side, mate.alignment, epsilon)
    ?? solveCircularEdgeCylinderCoincident(side, mate.alignment, epsilon)
    ?? solveCircularEdgeConeCoincident(side, mate.alignment, epsilon)
    ?? solveConeConeCoincident(side, mate.alignment, epsilon);
}
